import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CompanyModuleEnum,
  CustomerGroupDemandFilter,
  GroupDemandListPreviewOutDTO,
  GroupDemandStateEnum,
  StateEnum,
  SupplierNotificationTypeEnum,
  Pageable,
} from '@api/ringil-api.v2';
import { formatNumber, formatPercentage, isNotBlank, isNotNullOrUndefined, useLocalStorage } from '@utils/utils';
import useNavigation from '@hooks/useNavigation';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';
import createGDNameDialog from './dialog/ChangeGDNameDialog';
import { Link } from 'react-router-dom';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig, FilterType,
  OptionType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import { useMediaQuery } from 'react-responsive';
import COTag from '@components/FigmaComponents/COTag.tsx';
import ESStatusBar from '@components/FigmaComponents/ESStatusBar.tsx';
import SvgEdit from '@components/ringil3/icons/buttons/Edit.tsx';
import RingilMoreButtons, { MenuSection } from '@app/pages/RingilMoreButtons/RingilMoreButtons';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgMoney from '@components/ringil3/icons/Money.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { useTranslation } from 'react-i18next';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import clsx from 'clsx';
import SvgChevron from '@icons/chevron.svg?react';
import { formatPrice } from '@utils/priceUtils.ts';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import SecuredArea from '@components/security/SecuredArea';
import useCountries from '@hooks/use-countries/useCountries.tsx';
import CountryFlag from '@components/framework/CountryFlag/CountryFlag.tsx';
import SvgBuilding from '@icons/building.svg?react';
import SvgGlobe from '@icons/globe.svg?react';
import SvgCrates from '@icons/crates.svg?react';
import SvgWeight from '@icons/weight.svg?react';
import SvgNote from '@icons/note.svg?react';

enum InfoTagEnum {
  ALL = 'all',
  GOODS_TOTAL_COSTS = 'goodsTotalCosts',
  SUPPLIER = 'supplier',
  COUNTRY_OF_ORIGIN = 'countryOfOrigin',
  HANDLING_UNITS = 'handlingUnits',
  WEIGHT = 'weight',
  NOTE = 'note',
}

const ResponsiveCustomerGroupDemandList: React.FC= () => {
  const { t } = useTranslation('common');
  const { tr } = useTranslationLgs();
  const { listGroupDemands, cuDeleteGroupDemand, lovAllCompaniesByInheritModule } = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const countries = useCountries();
  const countryNameByCode = useMemo(() => new Map(countries.map(c => [c.code, c.name])), [countries]);
  const countryOptions: OptionType[] = useMemo(
    () => (countries ?? []).map(c => ({ label: c.name, value: c.code, active: true, icon: <CountryFlag code={c.code} /> })),
    [countries],
  );
  const [supplierOptions, setSupplierOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    lovAllCompaniesByInheritModule(CompanyModuleEnum.SUPPLIER_NOTIFICATION, d => {
      setSupplierOptions((d ?? []).map(c => ({ label: c.name, value: c.id, active: true })));
    });
  }, []);

  const initialExpandedInfoTags: Record<InfoTagEnum, boolean> = Object.values(InfoTagEnum).reduce(
    (acc, key) => ({ ...acc, [key]: false }),
    {} as Record<InfoTagEnum, boolean>,
  );
  const [expandedInfoTags, setExpandedInfoTags] = useLocalStorage<Record<InfoTagEnum, boolean>>(
    'CUSTOMER_GROUP_DEMAND_LIST_EXPANDED_INFO_TAGS',
    initialExpandedInfoTags,
  );

  const enc = (applicationId: string) => btoa(applicationId).replace(/[/=]/g, '');
  const navigation = useNavigation();

  const loadDataApi = (
    filter: CustomerGroupDemandFilter,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<GroupDemandListPreviewOutDTO>) => void) => {
    listGroupDemands(
      pageable,
      filter,
      data => {
        onSuccess(data);
      }
    )
  };

  const deleteGD = (id: string) => {
    cuDeleteGroupDemand(id, () => {
      tableRef.current?.reloadData();
    });
  };

  const toggleInfoExpansion = (key: InfoTagEnum) => {
    setExpandedInfoTags(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const setAllTagsExpanded = (expanded: boolean) => {
    setExpandedInfoTags(_ => {
      const allInfoTagKeys = Object.values(InfoTagEnum);
      const newState = {} as Record<InfoTagEnum, boolean>;
      allInfoTagKeys.forEach(key => {
        newState[key] = expanded;
      });
      return newState;
    });
  };

  const handleTagClick = (key: InfoTagEnum) => {
    toggleInfoExpansion(key);
  };

  const handleAllTagClick = (key: InfoTagEnum) => {
    setAllTagsExpanded(!expandedInfoTags[key]);
  };

  const { setGroupDemand, ChangeGDNameDialog } = createGDNameDialog();

  const isMobile = useMediaQuery({ maxWidth: 1024 });
  const tableRef = useRef<{ reloadData: () => void, selectedObjects: GroupDemandListPreviewOutDTO[], clear: () => void }>(null);

  const renderGroupDemandId = (row: GroupDemandListPreviewOutDTO) => {
    return (
      <div className={'flex flex-col items-start'} data-testid={`gd-row-${row.groupDemandApplicationId}`}>
        <Link
          data-testid={`gd-row-${row.groupDemandApplicationId}-applicationId`}
          to={navigation.createNavigationLink(navigation.urlFunctions.createCustomerGroupDemandDetail(row.groupDemandId))}>
          <span className="text-[var(--RINGIL-Orange,#FE5000)] font-dm-sans text-base font-medium leading-normal tracking-[-0.16px]">
            {row.groupDemandApplicationId}
          </span>
        </Link>
        <Link
          to={navigation.createNavigationLink(navigation.urlFunctions.createCustomerGroupDemandDetail(row.groupDemandId))}>
          <span className="text-[var(--RINGIL-Orange,#FE5000)] font-dm-sans text-base font-medium leading-normal tracking-[-0.16px]">
            {row.groupDemandName}
          </span>
        </Link>
        <SecuredArea
          id="row-created-by" instance={row.groupDemandApplicationId}
          visible={[{ type: 'data', label: 'createdByUserName present', satisfied: isNotBlank(row.createdByUserName) }]}
          label="Created by"
        >
          <CreatedByText value={row.createdByUserName}/>
        </SecuredArea>
      </div>
    );
  };

  const renderGroupDemandState = (row: GroupDemandListPreviewOutDTO) => {
    const totalResponses = row.assignedCarriersCount ? row.assignedCarriersCount: 0;
    const currentResponses = row.repliedCarriersCount ? row.repliedCarriersCount : 0;
    const percent = totalResponses > 0 ? (currentResponses / totalResponses) * 100 : 0;
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span data-testid={`gd-row-${row.groupDemandApplicationId}-validTill`} className="flex flex-col items-start gap-2 text-[#182230] text-sm font-normal font-dm-sans">
        {tr(`ListCustomerGroupDemand.validTill`, `Valid till`)} {(() => {
          const hasNew = isNotNullOrUndefined(row.utcEnd) && isNotNullOrUndefined(row.timezoneId);
          return stringDateTimeWithLocaleFromApiStringInTz(
            hasNew ? row.utcEnd : row.validTill,
            hasNew ? row.timezoneId : (loggedUser?.defaultTimezoneId ?? 'Europe/Prague'),
          );
        })()}
          <COTimezone dataTestId={`gd-row-${row.groupDemandApplicationId}-validTill-timezone`} timezoneId={loggedUser?.defaultTimezoneId} />
        </span>
        <div
          style={{ display: 'flex', alignItems: 'center', width: '100%' }}
          data-testid={`gd-row-${row.groupDemandApplicationId}-tender`}
          data-offers-count={String(currentResponses)}
          data-total-count={String(totalResponses)}
        >
          <span
            data-testid={`gd-row-${row.groupDemandApplicationId}-tender-offersCount`}
            data-offers-count={currentResponses}
            style={{ display: 'none' }}
          />
          <span
            data-testid={`gd-row-${row.groupDemandApplicationId}-tender-totalCount`}
            data-total-count={totalResponses}
            style={{ display: 'none' }}
          />
          <ESStatusBar
            startLabel={tr('ListCustomerGroupDemand.odpov', 'odpov.')}
            startValue={currentResponses}
            percentage={percent > 100 ? 100 : percent}
            endLabel={tr('ListCustomerGroupDemand.total', 'z')}
            endValue={totalResponses}
          />
        </div>
      </div>
    );
  };

  const renderNotifications = (row: GroupDemandListPreviewOutDTO) => {
    const notifications = row.supplierNotifications

    return (
      <div className={'flex flex-col items-start gap-2'}>
        <SecuredArea
          id="row-notifications" instance={row.groupDemandApplicationId}
          visible={[{ type: 'data', label: 'supplierNotifications present', satisfied: notifications.length > 0 }]}
          label="Supplier notifications"
        >
          {notifications.map((notification, index) => (
            <div key={index}>
              <COTag
                variant={'default'}
                label={tr('ListCustomerGroupDemand.notification', 'Notification')}
                >
                <Link
                  data-testid={`gd-row-${row.groupDemandApplicationId}-notification-${notification.supplierNotificationApplicationId}`}
                  to={navigation.createNavigationLink(
                    navigation.urlFunctions.createSupplierNotificationDetail(notification.supplierNotificationId, SupplierNotificationTypeEnum.EFXX),
                  )}
                >
                  {notification.supplierNotificationApplicationId}
                </Link>
              </COTag>
            </div>
          ))}
        </SecuredArea>
      </div>
    );
  };

  const renderAgreedPrice = (row: GroupDemandListPreviewOutDTO) => {
    const hasAgreedPrice = row.state === StateEnum.FINISHED && isNotNullOrUndefined(row.sumFinalPrice);
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <SecuredArea
          id="row-agreed-price" instance={row.groupDemandApplicationId}
          visible={[
            {
              type: 'state',
              label: 'state === FINISHED and sumFinalPrice present',
              satisfied: hasAgreedPrice,
              description: 'Shown only on a finished group demand with an agreed carrier price',
              byDesign: true,
            },
          ]}
          label="Agreed price"
          description="The carrier price agreed for the group demand"
        >
          {row.state === StateEnum.FINISHED && row.sumFinalPrice && (
            <>
              <PriceTag
                price={row.sumFinalPrice.price}
                currency={row.sumFinalPrice.currency}
                dataTestId={`gd-row-${row.groupDemandApplicationId}-agreedPrice`}
                tooltip={tr('ListCustomerGroupDemand.agreedCarrierPriceTooltip', 'Agreed carrier price: ') + row.sumFinalPrice.price + ' ' + row.sumFinalPrice.currency}
              />
              <COTag
                variant={'caption'}
                label={tr(`ListCustomerGroupDemand.percentageNew`, `Cena přepravy vůči hodnotě zboží: `)}>
                {formatPercentage(row.percentOfOrder)} %
              </COTag>
            </>
          )}
        </SecuredArea>
      </div>
    );
  };

  const renderAdditionalInfo = (row: GroupDemandListPreviewOutDTO) => {
    const goodsTotalValue = formatPrice(row.goodsTotalValue.amount, row.goodsTotalValue.currencyCode);
    const sns = row.supplierNotifications ?? [];

    const hasGoodsTotalValue = row.goodsTotalValue.amount > 0;
    const hasSuppliers = sns.some(sn => isNotBlank(sn.supplierName));
    const hasCountries = sns.some(sn => isNotBlank(sn.supplierLocationCountry));
    const hasHandlingUnits = sns.some(sn => isNotNullOrUndefined(sn.handlingUnitsCount));
    const hasWeight = sns.some(sn => isNotNullOrUndefined(sn.totalWeight));
    const internalNotes = [...new Set(sns.filter(sn => isNotBlank(sn.internalNote)).map(sn => sn.internalNote))];
    const isAdditionalInfoAvailable = hasGoodsTotalValue || hasSuppliers || hasCountries || hasHandlingUnits || hasWeight || internalNotes.length > 0;

    // SN slots stay order-aligned across the supplier/country tags; missing values render as em dash.
    // Dedup on the (supplier, country) pair — per-field dedup would break the alignment.
    const supplierCountryPairs = sns
      .map(sn => ({
        supplier: sn.supplierName ?? '—',
        country: sn.supplierLocationCountry ? countryNameByCode.get(sn.supplierLocationCountry) ?? sn.supplierLocationCountry : '—',
      }))
      .filter((pair, index, pairs) => pairs.findIndex(p => p.supplier === pair.supplier && p.country === pair.country) === index);
    const suppliersText = supplierCountryPairs.map(p => p.supplier).join(', ');
    const countriesText = supplierCountryPairs.map(p => p.country).join(', ');
    const handlingUnitsText = `${formatNumber(sns.reduce((acc, sn) => acc + (sn.handlingUnitsCount ?? 0), 0), 0)} ${tr('QtyUnit.MJ', 'MJ')}`;
    const weightText = `${formatNumber(sns.reduce((acc, sn) => acc + (sn.totalWeight ?? 0), 0), 0)} kg`;
    const notesText = internalNotes.join(' | ');

    return (
      <div className="flex flex-col gap-2 items-start">
        <div className="flex flex-wrap gap-2">
          <SecuredArea
            id="row-tag-all" instance={row.groupDemandApplicationId}
            visible={[{ type: 'data', label: 'any additional info available', satisfied: isAdditionalInfoAvailable }]}
            label="Expand-all tag"
          >
            <ExpandableInfoAllTag
              id={`all_${enc(row.groupDemandApplicationId)}`}
              onClick={() => handleAllTagClick(InfoTagEnum.ALL)}
              tooltipText={expandedInfoTags['all'] ? tr(`expandableInfoTag.collapseAll`, 'Collapse all') : tr(`expandableInfoTag.expandAll`, 'Expand all')}
              icon={
                <SvgChevron className={clsx('text-content-brand',
                  expandedInfoTags['all'] && 'rotate-180')}
                />
              }
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-goods-total-value" instance={row.groupDemandApplicationId}
            visible={[{ type: 'data', label: 'goodsTotalValue > 0', satisfied: hasGoodsTotalValue }]}
            label="Goods total value tag"
          >
            <ExpandableInfoTag
              id={`goodsTotalValue_${enc(row.groupDemandApplicationId)}`}
              dataTestId={`gd-row-${row.groupDemandApplicationId}-additional-orderValue`}
              expanded={expandedInfoTags[InfoTagEnum.GOODS_TOTAL_COSTS]}
              onClick={() => handleTagClick(InfoTagEnum.GOODS_TOTAL_COSTS)}
              label={tr('ListCustomerGroupDemand.goodsTotalValue', 'Total value of goods: ')}
              text={goodsTotalValue}
              icon={<SvgMoney />}
              count={1}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-supplier" instance={row.groupDemandApplicationId}
            visible={[{ type: 'data', label: 'supplierName present', satisfied: hasSuppliers }]}
            label="Supplier tag"
          >
            <ExpandableInfoTag
              id={`supplier_${enc(row.groupDemandApplicationId)}`}
              dataTestId={`gd-row-${row.groupDemandApplicationId}-additional-supplier`}
              expanded={!!expandedInfoTags[InfoTagEnum.SUPPLIER]}
              onClick={() => handleTagClick(InfoTagEnum.SUPPLIER)}
              label={tr('ListCustomerGroupDemand.supplier', 'Supplier: ')}
              text={suppliersText}
              icon={<SvgBuilding />}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-country-of-origin" instance={row.groupDemandApplicationId}
            visible={[{ type: 'data', label: 'supplierLocationCountry present', satisfied: hasCountries }]}
            label="Country of origin tag"
          >
            <ExpandableInfoTag
              id={`countryOfOrigin_${enc(row.groupDemandApplicationId)}`}
              dataTestId={`gd-row-${row.groupDemandApplicationId}-additional-countryOfOrigin`}
              expanded={!!expandedInfoTags[InfoTagEnum.COUNTRY_OF_ORIGIN]}
              onClick={() => handleTagClick(InfoTagEnum.COUNTRY_OF_ORIGIN)}
              label={tr('ListCustomerGroupDemand.countryOfOrigin', 'Country of origin: ')}
              text={countriesText}
              icon={<SvgGlobe />}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-handling-units" instance={row.groupDemandApplicationId}
            visible={[{ type: 'data', label: 'handlingUnitsCount present', satisfied: hasHandlingUnits }]}
            label="Handling units tag"
          >
            <ExpandableInfoTag
              id={`handlingUnits_${enc(row.groupDemandApplicationId)}`}
              dataTestId={`gd-row-${row.groupDemandApplicationId}-additional-handlingUnits`}
              expanded={!!expandedInfoTags[InfoTagEnum.HANDLING_UNITS]}
              onClick={() => handleTagClick(InfoTagEnum.HANDLING_UNITS)}
              label={tr('ListCustomerGroupDemand.handlingUnits', 'Handling units: ')}
              text={handlingUnitsText}
              icon={<SvgCrates />}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-weight" instance={row.groupDemandApplicationId}
            visible={[{ type: 'data', label: 'totalWeight present', satisfied: hasWeight }]}
            label="Weight tag"
          >
            <ExpandableInfoTag
              id={`weight_${enc(row.groupDemandApplicationId)}`}
              dataTestId={`gd-row-${row.groupDemandApplicationId}-additional-weight`}
              expanded={!!expandedInfoTags[InfoTagEnum.WEIGHT]}
              onClick={() => handleTagClick(InfoTagEnum.WEIGHT)}
              label={tr('ListCustomerGroupDemand.weight', 'Weight: ')}
              text={weightText}
              icon={<SvgWeight />}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-note" instance={row.groupDemandApplicationId}
            visible={[
              {
                type: 'ownership',
                label: 'internal note is visible only to users of the owning company (enforced BE-side)',
                satisfied: true,
                description: 'Owner only: the internal note is shown only to the company that owns the group demand',
                byDesign: true,
              },
              { type: 'data', label: 'internalNote present', satisfied: internalNotes.length > 0 },
            ]}
            label="Internal note tag"
            description="The group demand owner's internal note"
          >
            <ExpandableInfoTag
              id={`note_${enc(row.groupDemandApplicationId)}`}
              dataTestId={`gd-row-${row.groupDemandApplicationId}-additional-note`}
              expanded={!!expandedInfoTags[InfoTagEnum.NOTE]}
              onClick={() => handleTagClick(InfoTagEnum.NOTE)}
              text={notesText}
              textComponent={<span className="font-medium line-clamp-2 max-w-[229px] break-words">{notesText}</span>}
              icon={<SvgNote />}
            />
          </SecuredArea>
        </div>
      </div>
    );
  };

  const renderActions = (row: GroupDemandListPreviewOutDTO) => {
    const menuSections: MenuSection[] = [
      {
        id: 'actions',
        title: 'Actions',
        titleKey: 'ListCustomerGroupDemand.actionsSection',
        items: [
          {
            id: 'edit',
            label: tr('ListCustomerGroupDemand.edit', 'Edit'),
            icon: <SvgEdit />,
            onClick: () => {
              setGroupDemand({
                groupDemandName: row.groupDemandName,
                groupDemandId: row.groupDemandId,
              });
            },
            visible: true,
          },
          {
            id: 'delete',
            label: tr('ListCustomerGroupDemand.delete', 'Delete'),
            icon: <SvgDelete />,
            onClick: () => {
              deleteGD(row.groupDemandId);
            },
            visible: true,
            className: 'text-red-600 hover:text-red-800',
          },
        ],
      },
    ];

    return (
      <RingilMoreButtons
        sections={menuSections}
        buttonColor="#616061"
        menuPosition="right"
        onItemClick={(itemId) => {
          console.log(`Group demand action: ${itemId} for ${row.groupDemandId}`);
        }}
      />
    );
  };

  const columnsConfig: ColumnConfig<GroupDemandListPreviewOutDTO>[] = [
    {
      id: 'groupDemandApplicationId',
      header: 'ListCustomerGroupDemand.groupDemandNameAppId',
      headerDefault: 'Group demand ID',
      width: isMobile ? '100%' : '15%',
      sortable: true,
      isDefaultSort: true,
      field: 'groupDemandApplicationId',
      order: 1,
      render: renderGroupDemandId,
    },
    {
      id: 'state',
      header: 'ListCustomerGroupDemand.state',
      headerDefault: 'State',
      width: '30%',
      sortable: true,
      isDefaultSort: false,
      order: 1,
      render: renderGroupDemandState,
    },
    {
      id: 'supplierNotifications',
      header: 'ListCustomerGroupDemand.supplierNotifications',
      headerDefault: 'Supplier notifications',
      width: '20%',
      sortable: false,
      order: 2,
      render: renderNotifications,
    },
    {
      id: 'agreedPrice',
      header: 'ListCustomerGroupDemand.price',
      headerDefault: 'Agreed price',
      width: '20%',
      sortable: false,
      order: 3,
      render: renderAgreedPrice,
    },
    {
      id: 'additionalInfo',
      header: 'ListCustomerGroupDemand.additionalInfo',
      headerDefault: 'Additional info',
      width: '15%',
      sortable: false,
      order: 4,
      render: renderAdditionalInfo,
    },
    {
      id: 'actions',
      header: '',
      headerDefault: '',
      width: '5%',
      sortable: false,
      order: 5,
      render: renderActions,
    },
  ]

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr(`ListCustomerGroupDemand.groupDemandNameAppId`, `Group demand ID`),
      value: ['applicationId'],
      dataTestId: 'gd-filter-applicationId',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr(`ListCustomerGroupDemand.supplierNotificationApplicationId`, `Notification ID`),
      value: ['supplierNotificationApplicationId'],
      dataTestId: 'gd-filter-snApplicationId',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr(`ListCustomerGroupDemand.groupDemandName`, `Group demand ID`),
      value: ['groupDemandName'],
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr(`ListCustomerGroupDemand.createdBy`, `Group demand ID`),
      value: ['createdByUserName'],
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`ListCustomerGroupDemand.supplierId`, `Supplier`),
      value: ['supplierIds'],
      options: supplierOptions,
      dataTestId: 'gd-filter-supplierIds',
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr(`ListCustomerGroupDemand.supplierLocationCountry`, `Country of origin`),
      value: ['supplierLocationCountry'],
      options: countryOptions,
      dataTestId: 'gd-filter-supplierLocationCountry',
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`ListCustomerGroupDemand.showActiveOnly`, `State`),
      value: ['groupDemandState'],
      options: Object.keys(GroupDemandStateEnum).map(i => ({
        label: t(`Shipments.${i}`, i),
        value: i,
        active: true,
      })),
    },
  ]

  return (
    <>
      <div className={'flex flex-col gap-2'}>
        <BreadCrumbComponent items={[{ label: tr(`ListCustomerGroupDemand.groupDemands`, `Group demands`) }]} />
        <COHeadline title={tr(`ListCustomerGroupDemand.groupDemands`, `Group demands`)} variant={'h1'} />
      </div>
      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'list_customer_group_demand'}
        floatingMassActions={true}
      />

      <ChangeGDNameDialog
        onCancel={() => {
        }}
        onCompleted={() => {
          tableRef.current?.reloadData();
        }}
      />
    </>
  );
};

export default ResponsiveCustomerGroupDemandList;


// Start text component for status bar
interface CreatedByProps {
  value?: string;
}

const CreatedByText: React.FC<CreatedByProps> = ({ value }) => {
  return (
    <>
      {value && <span className="self-stretch text-[#616061] font-dm-sans text-xs font-normal tracking-[0.24px]">{value}</span>}
    </>
  );
};

interface PriceTagProps {
  price: number;
  currency: string;
  tooltip?: string;
  dataTestId?: string;
}

const PriceTag: React.FC<PriceTagProps> = ({ price, currency, tooltip, dataTestId }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const formattedPrice = formatPrice(price, currency);

  if (tooltip) {
    return (
      <div className="relative inline-block">
        <span
          data-testid={dataTestId}
          className="text-[#616061] font-dm-sans text-base font-normal tracking-[-0.16px] cursor-pointer hover:text-[#182230] transition-colors"
          onClick={() => setShowTooltip(!showTooltip)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {formattedPrice}
        </span>
        {showTooltip && (
          <div className="absolute bottom-full left-0 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg whitespace-nowrap z-10">
            {tooltip}
            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
          </div>
        )}
      </div>
    );
  }

  return (
    <span className="text-[#616061] font-dm-sans text-base font-normal tracking-[-0.16px]" data-testid={dataTestId}>
      {formattedPrice}
    </span>
  );
};

